import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Chevron, { DIRECTIONS } from 'components/Chevron';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const ELLIPSIS = '...';

const getDisplayedPages = (totalPages, currentPage) => {
  // Ensures user always has access to first and last page while also showing the current one
  if (totalPages <= 5) return [...Array(totalPages).keys()].map(i => i + 1);

  if (currentPage > 3 && currentPage < totalPages - 2) {
    return [1, ELLIPSIS, currentPage, ELLIPSIS, totalPages];
  }

  return currentPage <= 3
    ? [1, 2, 3, ELLIPSIS, totalPages]
    : [1, ELLIPSIS, totalPages - 2, totalPages - 1, totalPages];
};

const Pagination = ({ className, totalPages, currentPage, onClick }) => {
  const pages = getDisplayedPages(totalPages, currentPage);

  return (
    <div
      className={classNames(`Pagination`, {
        [className]: className,
      })}
    >
      <button
        className={classNames('Pagination__button', 'Pagination__button--nav')}
        disabled={currentPage === 1}
        onClick={() => onClick(currentPage - 1)}
        aria-label={I18n.localize('pagination.previous')}
        data-testid="pagination-previous"
      >
        <Chevron direction={DIRECTIONS.LEFT} />
      </button>

      {pages.map(page =>
        page === ELLIPSIS ? (
          <span
            className={classNames('Pagination__button', 'Pagination__button--ellipsis')}
            key={`pagination-${page}`}
          >
            {ELLIPSIS}
          </span>
        ) : (
          <button
            className={classNames('Pagination__button', {
              'Pagination__button--active': page === currentPage,
            })}
            disabled={page === currentPage}
            aria-label={I18n.localize('pagination.indicator', { page })}
            aria-current={page === currentPage ? 'page' : undefined}
            onClick={() => onClick(page)}
            data-testid={`pagination-${page}`}
            key={`pagination-${page}`}
          >
            {page}
          </button>
        )
      )}

      <button
        className={classNames('Pagination__button', 'Pagination__button--nav')}
        disabled={currentPage === totalPages}
        onClick={() => onClick(currentPage + 1)}
        aria-label={I18n.localize('pagination.next')}
        data-testid="pagination-next"
      >
        <Chevron direction={DIRECTIONS.RIGHT} />
      </button>

      <style jsx>
        {`
          .Pagination {
            display: flex;
            justify-content: center;
            gap: ${Theme.get('spacing-px')};
            max-width: 384px;
            margin: 0 auto;
          }

          .Pagination__button {
            all: unset;
            text-align: center;
            cursor: pointer;
            color: ${Theme.get('colors-black')};
            border-radius: ${Theme.get('border-radius-px')};
            font-family: ${Theme.get('font-family')};
            font-size: ${Theme.get('font-size-small-px')};
            width: calc(100% / 7);
            max-width: 48px;
            aspect-ratio: 1;
            transition-property: background-color;
            transition-duration: 0.1s;
            transition-timing-function: ${Theme.get('transition-timing-ease-in')};
          }

          .Pagination__button[disabled] {
            cursor: auto;
          }

          .Pagination__button--nav[disabled] {
            opacity: 0.5;
          }

          .Pagination__button--active,
          .Pagination__button:not(.Pagination__button--ellipsis):not(
              .Pagination__button--nav
            ):hover {
            background-color: ${Theme.get('colors-black')};
            color: ${Theme.get('colors-white')};
          }
        `}
      </style>
    </div>
  );
};

Pagination.propTypes = {
  onClick: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  className: PropTypes.string,
};

Pagination.defaultProps = {
  className: '',
};

export default Pagination;
